/**
 * CorrectDrawer — web port of iOS
 * `client-native/src/features/flight-school/components/CorrectDrawer.tsx`.
 *
 * The confirm-to-continue success drawer the LESSON-variant activities show on a
 * correct answer (iOS does NOT auto-advance in the lesson track — it slides up a
 * green drawer with the word/translation and a "CONTINUE" button, gating the
 * advance on the user pressing it). Feed-variant activities keep auto-advancing
 * and never mount this.
 *
 * Mirrors iOS props: optional `spanishWord`/`englishWord` translation line
 * (suppressed via `hideTranslation`), and `onContinue` (the host passes
 * `onFinish(false)` so the correct sound isn't double-played).
 */
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { colors, fontFamily } from "@/ui";
import { useSound } from "@/hooks/useSound";
import checkmarkImage from "@/features/flight-school/assets/checkmark.png";

export interface CorrectDrawerProps {
  visible: boolean;
  /** Spanish word for the "spanish = english" translation line. */
  spanishWord?: string;
  /** English translation for the translation line. */
  englishWord?: string;
  /** Hide the translation line entirely (Fill/Sentence pass this). */
  hideTranslation?: boolean;
  title?: string;
  /**
   * Play the correct sound the moment the drawer slides up (iOS CorrectDrawer
   * plays it on becoming visible — CorrectDrawer.tsx:88-90 — so the reward
   * lands at success time, not on the CONTINUE tap). Defaults true.
   */
  playSuccessSound?: boolean;
  /** Called when the user presses Continue (host advances the lesson). */
  onContinue: () => void;
}

export default function CorrectDrawer({
  visible,
  spanishWord,
  englishWord,
  hideTranslation = false,
  title = "Correct!",
  playSuccessSound = true,
  onContinue,
}: CorrectDrawerProps) {
  // Guard against a double-fire if Continue is tapped twice during the slide.
  const continuedRef = useRef(false);
  const { playCorrectSound } = useSound();
  // Play the reward sound once, on the visible transition (iOS parity).
  const wasVisibleRef = useRef(false);

  useEffect(() => {
    if (visible) {
      continuedRef.current = false;
      if (!wasVisibleRef.current && playSuccessSound) {
        playCorrectSound();
      }
      wasVisibleRef.current = true;
    } else {
      wasVisibleRef.current = false;
    }
  }, [visible, playSuccessSound, playCorrectSound]);

  if (!visible) return null;

  const handleContinue = () => {
    if (continuedRef.current) return;
    continuedRef.current = true;
    onContinue();
  };

  const showTranslation = !hideTranslation && spanishWord && englishWord;

  // Portal to <body> + position:fixed so the drawer reaches the real viewport
  // bottom (covering the lesson's "Swipe up to skip" footer), not just the
  // bottom of the activity area. Anchored as an absolute child of the activity
  // it stopped above the footer; in the lesson track that left a gap below the
  // green. Phone-width centered to match the lesson column on desktop. Escaping
  // to body also dodges the TransitionsVertical translateY transforms that would
  // otherwise trap a fixed element inside the activity.
  return createPortal(
    <div
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        margin: "0 auto",
        width: "100%",
        maxWidth: 600,
        boxSizing: "border-box",
        backgroundColor: colors.green,
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        padding: "24px 44px",
        paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)",
        boxShadow: "0 -4px 12px rgba(0,0,0,0.15)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        animation: "correctDrawerSlideUp 280ms cubic-bezier(0.16, 1, 0.3, 1)",
        zIndex: 50,
      }}
    >
      <style>
        {`@keyframes correctDrawerSlideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        @keyframes correctDrawerCheckIn {
          from { transform: scale(0); }
          to { transform: scale(1); }
        }`}
      </style>

      <img
        src={checkmarkImage}
        alt=""
        style={{
          width: 52,
          height: 52,
          marginBottom: 8,
          objectFit: "contain",
          animation: "correctDrawerCheckIn 0.18s ease-out 0.25s both",
        }}
      />

      <div
        style={{
          fontSize: 28,
          fontWeight: 700,
          color: colors.white,
          marginBottom: 24,
          textAlign: "center",
        }}
      >
        {title}
      </div>

      {showTranslation && (
        <div
          style={{
            fontSize: 18,
            fontWeight: 500,
            color: "rgba(255,255,255,0.9)",
            textAlign: "center",
            marginBottom: 20,
          }}
        >
          {spanishWord} = {englishWord}
        </div>
      )}

      <button
        type="button"
        onClick={handleContinue}
        style={{
          width: "100%",
          minHeight: 56,
          borderRadius: 16,
          border: "none",
          background: colors.white,
          color: colors.green,
          fontFamily,
          fontWeight: 700,
          fontSize: "1rem",
          letterSpacing: "0.02em",
          cursor: "pointer",
          boxShadow: "0 4px 0 rgba(0,0,0,0.15)",
          touchAction: "manipulation",
        }}
      >
        CONTINUE
      </button>
    </div>,
    document.body
  );
}
